/**
 * 定位工具函数
 * 支持微信和普通浏览器的定位功能
 */


// 默认定位数据 - 珠海市中心坐标
export const DEFAULT_LOCATION = {
  latitude: 22.276797,
  longitude: 113.569532,
  source: 'default'
};

// 缓存相关常量
const LOCATION_CACHE_KEY = 'user_location_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1小时，单位毫秒

/**
 * 从缓存中获取位置信息
 * @returns {object|null} 缓存的位置信息或null
 */
const getLocationFromCache = () => {
  try {
    const cachedData = localStorage.getItem(LOCATION_CACHE_KEY);
    if (cachedData) {
      const { location, timestamp } = JSON.parse(cachedData);
      const now = Date.now();
      
      // 检查缓存是否在有效期内
      if (now - timestamp < CACHE_DURATION) {
        console.log('使用缓存的位置信息');
        return location;
      }
    }
  } catch (error) {
    console.error('读取位置缓存失败', error);
  }
  return null;
};

/**
 * 将位置信息存入缓存
 * @param {object} location 位置信息
 */
const cacheLocation = (location) => {
  try {
    const cacheData = {
      location,
      timestamp: Date.now()
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
    console.log('位置信息已缓存');
  } catch (error) {
    console.error('缓存位置信息失败', error);
  }
};

/**
 * 获取用户位置信息
 * @param {object} wx - 微信JS-SDK对象（可选）
 * @param {boolean} configured - 微信JS-SDK是否已配置（可选）
 * @param {boolean} forceRefresh - 是否强制刷新位置，忽略缓存（可选）
 * @returns {Promise} 返回包含位置信息的Promise
 */
export const getUserLocation = (wx, configured, forceRefresh = false) => {
  return new Promise((resolve) => {
    // 如果不强制刷新且缓存有效，则使用缓存的位置
    if (!forceRefresh) {
      const cachedLocation = getLocationFromCache();
      if (cachedLocation) {
        resolve(cachedLocation);
        return;
      }
    }
    
    // 判断是否在微信环境中
    const isWechat = /MicroMessenger/i.test(navigator.userAgent);
    
    if (isWechat && wx && configured) {
      // 微信环境使用微信JS-SDK定位
      wx.getLocation({
        type: 'wgs84', // 默认为wgs84的gps坐标，如果要返回直接给openLocation用的火星坐标，可传入'gcj02'
        success: (res) => {
          const location = {
            latitude: res.latitude,
            longitude: res.longitude,
            source: 'wechat'
          };
          cacheLocation(location);
          resolve(location);
        },
        fail: (err) => {
          console.error('微信定位失败', err);
          // 微信定位失败，尝试使用浏览器定位
          getBrowserLocation().then((location) => {
            cacheLocation(location);
            resolve(location);
          }).catch(() => {
            // 浏览器定位也失败，返回默认位置
            resolve(DEFAULT_LOCATION);
          });
        }
      });
    } else {
      // 普通浏览器环境使用HTML5 Geolocation API
      getBrowserLocation().then((location) => {
        cacheLocation(location);
        resolve(location);
      }).catch(() => {
        // 浏览器定位失败，返回默认位置
        resolve(DEFAULT_LOCATION);
      });
    }
  });
};

/**
 * 浏览器定位
 * @returns {Promise} 返回包含位置信息的Promise
 */
const getBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('浏览器不支持定位功能'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          source: 'browser'
        });
      },
      (error) => {
        console.error('浏览器定位失败', error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  });
};

/**
 * 计算两点之间的距离（单位：米）
 * @param {number} lat1 - 点1纬度
 * @param {number} lon1 - 点1经度
 * @param {number} lat2 - 点2纬度
 * @param {number} lon2 - 点2经度
 * @returns {number} 距离（米）
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // 地球半径（米）
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};